import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { SearchNavBar } from '../../components/ui';
import { AppStrings, purposeValEnhancer } from '../../utils';
import { remittanceActions } from '../../state/remittanceSlice';
import Typography from '../../../../components/ui/Typography';
import DocumentItem from './DocumentItem';
import { PreviewDocsModal } from '../../components/modal';
import MixpanelConfig from '../../../../constants/MixpanelConfig';

const RemittanceDocuments: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient, documents } = useAppSelector((state) => state.remittance);

  const documentsList = documents?.documentsList?.list || [];
  const leadDocumentsList = documents?.leadDocumentsList?.list || [];
  const loading = documents?.documentsList?.loading || documents?.leadDocumentsList?.loading;
  const error = documents?.documentsList?.error || documents?.leadDocumentsList?.error;
  const isSubmitDisabled = documents?.recipientLeadDocs.some(doc => doc.isRequired && !doc.leadId);

  {/* Initial API call and condition when user came to this page */}
  useEffect(() => {
    dispatch(remittanceActions.fetchDocumentsListRequested({
      purposeCode: purposeValEnhancer(calculator?.amountCalculation?.transaction_purpose),
      currency: calculator?.amountCalculation?.dest_currency,
      recipientType: recipient?.selectedRecipient?.type === null ? "Others" : recipient?.selectedRecipient?.type.replace(/\s+/g, "_"),
      userId: remitter?.selectedRemitter?.id,
      loanDisbursementLetter:calculator?.amountCalculation?.isEducation
    }));
    dispatch(remittanceActions.fetchLeadDocumentsListRequested({
      purposeCode: calculator?.amountCalculation?.purposeCode,
      remitterId: remitter?.selectedRemitter?.userKey,
      recipientId: recipient?.selectedRecipient?.id
    }));
  }, []);

  useEffect(() => {
    if(remitterState?.progresStep?.activeStepNumber < 4) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (documentsList.length == 0 || documents?.documentsList?.loading || documents?.leadDocumentsList?.loading) return;
  
    const normalize = (s: string) => s?.toLowerCase().replace(/[\s_-]/g, "").trim();
  
    const mergedList = documentsList.map((doc, index) => {
      const match = leadDocumentsList.find((lead) => 
        normalize(lead.docName) === normalize(doc.document)
      );
  
      return {
        ...doc,
        leadsData: match || null,
        leadId: match?.id || null,
        id: index,
        docName: match?.docName || "",
        loading: false,
        error: ""
      };
    });

    dispatch(remittanceActions.setRecipientLeadDocsState(mergedList));
  
  }, [documentsList, leadDocumentsList, documents?.documentsList?.loading, documents?.leadDocumentsList?.loading]);

  return(
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px]">
        <ToastContainer autoClose={3000} position="top-right" />

        <div className="flex flex-col">

        <SearchNavBar
          title={AppStrings.DOCUMENTS_SCREEN.RECIPIENT_DOCUMENTS}
          addButtonTitle={AppStrings.BUTTON.SUBMIT}
          onAddClick={() => {
            dispatch(remittanceActions.resetReviewState());
            dispatch(remittanceActions.setActiveStepNumber(5));
            dispatch(remittanceActions.resetReviewState());
            dispatch(remittanceActions.resetTransactionDetailState());
            navigate("/send/review");
            MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Documents_Submit, {
              authState: authState,
              remitterState: remitterState
            });
          }}
          titleAndButton={false}
          disabled={loading || error != null || isSubmitDisabled ? true : false}
        />

        {error && (
          <div className="p-4 flex justify-center items-center">
            <Typography tagType="div" text={error} className="text-sm font-normal text-red-100" />
          </div>
        )}

        {loading && (
          <div className="p-4 flex justify-center items-center">
            <div className="animate-spin h-6 w-6 border-2 border-t-blue-500 rounded-full" />
          </div>
        )}

        {!loading && !error && (
          <div id="body" className="flex w-full h-screen">
            <div id="left" className="flex flex-col items-start">
      
              <div className="flex items-end justify-end gap-20 w-full">
              </div>
      
              <div className={`flex justify-start my-1 w-full gap-10 ${documents?.recipientLeadDocs?.length > 3 ? "flex-wrap" : "flex-row"}`}>
                {documents?.recipientLeadDocs?.map((item: any, index) => (
                  <DocumentItem 
                    item={item}
                    key={index}
                  />
                ))}
              </div>
      
              {documentsList?.length > 0 && calculator?.amountCalculation?.purposeCode !== "S0305" && (
                <p className="bg-gray-100 p-3 rounded-lg mb-4 w-full text-base">
                  <span className="font-semibold">{AppStrings.DOCUMENTS_SCREEN.BENEFICIARY_ID}</span>
                  {AppStrings.DOCUMENTS_SCREEN.BENEFICIARY_ID_MSG}
                </p>
              )}  
      
              {documentsList?.length > 0 && calculator?.amountCalculation?.purposeCode === "S1301" && (
                <p className="bg-gray-100 p-3 rounded-lg w-full text-base">
                  <span className="font-semibold">{AppStrings.DOCUMENTS_SCREEN.RELATIONSHIP_PROOF}</span>
                  {AppStrings.DOCUMENTS_SCREEN.RELATIONSHIP_PROOF_MSG}
                </p>
              )}
      
            </div>
          </div>
        )}

        </div>

      </div>

      <PreviewDocsModal
        open={remitterState?.modalType === "PREVIEW_DOCS"}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
      />
    </>

  );
};

export default RemittanceDocuments;